import { useMemo, useState } from 'react';
import { Checkbox, FormActions, Grid, Inline } from '../ui';
import type { PermissionCatalog, Role } from '../../lib/api/access.gen';

// PermissionMatrix (P01 §4.3) — grouped permission editor for a role. Staged
// client-side; Save is a single PATCH. System roles are read-only (clone to
// edit). Wildcards (domain:*) are expanded to concrete keys for display; Save
// sends the explicit key set. (Flag-masked/inherited-locked cell states and the
// wildcard tri-state toggle are refinements for a later pass.)

const expandToKeys = (permissions: string[], catalog: PermissionCatalog): Set<string> => {
  const byDomain = new Map(catalog.groups.map((g) => [g.domain, g.keys]));
  const out = new Set<string>();
  permissions.forEach((p) => {
    const wildcard = /^([a-z]+):\*$/.exec(p);
    const domain = wildcard ? wildcard[1] : undefined;
    if (domain) (byDomain.get(domain) ?? []).forEach((k) => out.add(k));
    else out.add(p);
  });
  return out;
};

const sameSet = (a: Set<string>, b: Set<string>): boolean => a.size === b.size && [...a].every((k) => b.has(k));

interface PermissionMatrixProps {
  role: Role;
  catalog: PermissionCatalog;
  canEdit: boolean;
  saving?: boolean;
  error?: string | null;
  onSave: (permissions: string[]) => void;
}

// Remount per role (key={role.id}) so staged state resets cleanly.
export function PermissionMatrix({ role, catalog, canEdit, saving, error, onSave }: PermissionMatrixProps): JSX.Element {
  const initial = useMemo(() => expandToKeys(role.permissions, catalog), [role, catalog]);
  const [selected, setSelected] = useState<Set<string>>(initial);
  const dirty = !sameSet(selected, initial);

  const toggle = (key: string) => {
    if (!canEdit) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-5">
      {!canEdit && (
        <p className="rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          System roles can’t be edited — use “Duplicate” to create an editable copy.
        </p>
      )}
      {catalog.groups.map((group) => (
        <div key={group.domain} className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group.label}</p>
          <Grid columns={{ base: 1, sm: 2 }} gap={2}>
            {group.keys.map((key) => (
              <Inline
                key={key}
                as="label"
                gap={2}
                className={`rounded-lg border border-border/60 px-3 py-2 text-sm ${
                  canEdit ? 'cursor-pointer hover:bg-muted/40' : 'opacity-80'
                }`}
              >
                <Checkbox value={selected.has(key)} onChange={() => toggle(key)} disabled={!canEdit} />
                <span className="font-mono text-xs text-foreground">{key}</span>
              </Inline>
            ))}
          </Grid>
        </div>
      ))}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {canEdit && dirty && (
        <div className="sticky bottom-0 border-t border-border/60 bg-background/90 py-3 backdrop-blur">
          <FormActions
            actions={[
              {
                id: 'discard',
                label: 'Discard',
                variant: 'ghost',
                onClick: () => setSelected(initial),
                disabled: Boolean(saving),
              },
              {
                id: 'save',
                label: saving ? 'Saving…' : 'Save changes',
                variant: 'primary',
                onClick: () => onSave([...selected]),
                disabled: Boolean(saving),
                loading: Boolean(saving),
              },
            ]}
          />
        </div>
      )}
    </div>
  );
}
